import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useTenant } from '../context/TenantContext';
import { ShoppingBag, ArrowRight, Trash2, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import ConfirmModal from './ConfirmModal';

export default function FloatingCheckoutBar() {
    const location = useLocation();
    const { cartTotal, itemCount, clearCart } = useCart();
    const { tenant } = useTenant();
    const [isDismissed, setIsDismissed] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    // Reset dismissal when cart is empty, so it pops up again when new items are added
    useEffect(() => {
        if (itemCount === 0) {
            setIsDismissed(false);
        }
    }, [itemCount]);

    // Hide if cart is empty, dismissed, or if user is already on cart/checkout or admin dashboard
    const isCartOrCheckoutRoute = location.pathname === '/cart' || 
                                  location.pathname === '/checkout' ||
                                  location.pathname.startsWith('/admin') || 
                                  location.pathname.startsWith('/kitchen') || 
                                  location.pathname.startsWith('/delivery');

    if (itemCount === 0 || isDismissed || isCartOrCheckoutRoute) {
        return null;
    }

    const primaryBgStyle = tenant?.primary_color ? { backgroundColor: tenant.primary_color } : {};

    return (
        <>
            <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 pointer-events-none animate-in slide-in-from-bottom duration-300">
                <div className="max-w-4xl mx-auto pointer-events-auto relative">
                    <div 
                        className="bg-slate-900 text-white p-3.5 sm:p-5 rounded-3xl shadow-2xl border border-white/10 flex flex-row items-center justify-between gap-3 backdrop-blur-xl bg-slate-900/95 relative pr-8 sm:pr-10"
                    >
                        {/* Left Info: Items & Total */}
                        <div className="flex items-center space-x-2.5 pl-1 min-w-0">
                            <div 
                                className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-primary-600 flex items-center justify-center text-white shadow-lg flex-shrink-0"
                                style={primaryBgStyle}
                            >
                                <ShoppingBag className="w-5.5 h-5.5 sm:w-6 sm:h-6" />
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-black text-sm sm:text-base tracking-tight text-white whitespace-nowrap">{itemCount} {itemCount === 1 ? 'Item' : 'Items'}</span>
                                    <span className="text-slate-500">•</span>
                                    <span className="font-black text-base sm:text-lg text-amber-400">₹{cartTotal.toFixed(2)}</span>
                                </div>
                                <p className="text-[10px] sm:text-[11px] text-slate-400 font-medium truncate">Taxes & delivery calculated at checkout</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Clear Cart Button */}
                            <button
                                onClick={() => setShowConfirm(true)}
                                className="p-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 rounded-2xl transition-colors border border-red-500/20 flex items-center justify-center"
                                title="Clear Cart"
                            >
                                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>

                            {/* Right Big Checkout Button */}
                            <Link
                                to="/cart"
                                className="btn-primary py-3 px-4 sm:py-3.5 sm:px-8 rounded-2xl font-black text-[10px] sm:text-sm uppercase tracking-widest flex items-center gap-2 shadow-xl transition-all hover:scale-105 active:scale-95"
                                style={primaryBgStyle}
                            >
                                <span>Checkout Now</span>
                                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </Link>
                        </div>

                        {/* Close/Dismiss Button */}
                        <button
                            onClick={() => setIsDismissed(true)}
                            className="absolute right-2 sm:right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10"
                            title="Dismiss"
                        >
                            <X className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={showConfirm}
                title="Clear Cart"
                message="Are you sure you want to empty your shopping cart? This action cannot be undone."
                confirmText="Clear Cart"
                cancelText="Cancel"
                onConfirm={() => {
                    clearCart();
                    setShowConfirm(false);
                }}
                onCancel={() => setShowConfirm(false)}
                variant="danger"
            />
        </>
    );
}
